import * as THREE from "three";

/**
 * Weapon definitions — ported from zombies' config.ts WeaponDef with the real
 * combat numbers (fire rate in RPM, mag/reserve, reload seconds, spread cone,
 * pellets, semi/auto). Guns are two-handed; melee swings instead of firing.
 */
export interface WeaponDef {
  id: string;
  name: string;
  color: number;   // procedural gun tint
  length: number;  // barrel length (world units) — longer = rifle
  damage: number;      // per hit/pellet (kept for forward-compat; no targets in the lab yet)
  fireRate: number;    // rounds (or swings) per minute
  magSize: number;     // 0 = no ammo system (melee)
  reserveAmmo: number;
  reloadTime: number;  // seconds
  range: number;       // hitscan max distance (world units)
  automatic: boolean;  // hold-to-fire
  pellets?: number;    // shotgun: pellets per shot
  spread?: number;     // radians of cone half-angle (default hipfire 0.03)
  melee?: boolean;     // cone swing instead of hitscan
  infiniteReserve?: boolean; // pistol never runs dry
  kick: number;        // recoil impulse per shot (0..1 scale)
}

// Numbers carried from zombies config.ts (M1911 / MP5 / AK-74 / BOOMSTICK + a bat).
export const WEAPONS: Record<string, WeaponDef> = {
  pistol: {
    id: "pistol", name: "Pistol", color: 0x9a9a9a, length: 0.18,
    damage: 34, fireRate: 350, magSize: 8, reserveAmmo: 80, reloadTime: 1.4, range: 100,
    automatic: false, infiniteReserve: true, kick: 0.35,
  },
  smg: {
    id: "smg", name: "SMG", color: 0x6a6a72, length: 0.26,
    damage: 24, fireRate: 750, magSize: 30, reserveAmmo: 240, reloadTime: 2.0, range: 90,
    automatic: true, kick: 0.2,
  },
  rifle: {
    id: "rifle", name: "Rifle", color: 0x3f4247, length: 0.34,
    damage: 40, fireRate: 600, magSize: 30, reserveAmmo: 270, reloadTime: 2.3, range: 120,
    automatic: true, kick: 0.28,
  },
  shotgun: {
    id: "shotgun", name: "Shotgun", color: 0x7a5230, length: 0.30,
    damage: 40, fireRate: 115, magSize: 8, reserveAmmo: 72, reloadTime: 2.1, range: 44,
    automatic: false, pellets: 12, spread: 0.10, kick: 0.85,
  },
  bat: {
    id: "bat", name: "Bat", color: 0x8a5a2a, length: 0.32,
    damage: 200, fireRate: 130, magSize: 0, reserveAmmo: 0, reloadTime: 0, range: 3.2,
    automatic: false, melee: true, kick: 0,
  },
};

/**
 * A blocky two-handed gun, authored in a GRIP-LOCAL frame: origin = where the
 * dominant hand grips, +Z = barrel/forward, +Y = up. `foreGrip` (returned) is the
 * local point where the support hand holds the fore-end. `muzzle` marks the tip.
 */
export function buildGunMesh(def: WeaponDef): { mesh: THREE.Group; foreGrip: THREE.Vector3; muzzle: THREE.Vector3 } {
  const g = new THREE.Group();
  const mat = new THREE.MeshLambertMaterial({ color: def.color });
  const dark = new THREE.MeshLambertMaterial({ color: 0x1c1c1e });
  const box = (m: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number): THREE.Mesh => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    g.add(mesh);
    return mesh;
  };
  const L = def.length;
  if (def.melee) {
    // a bat: fat tapered shaft along +Z from the grip, one-handed
    box(dark, 0.045, 0.045, 0.1, 0, 0, 0.03);                      // handle
    box(mat, 0.07, 0.07, L, 0, 0, 0.1 + L * 0.5);                  // shaft
    box(mat, 0.085, 0.085, L * 0.4, 0, 0, 0.1 + L * 0.9);          // fat end
    return { mesh: g, foreGrip: new THREE.Vector3(0, 0, 0.06), muzzle: new THREE.Vector3(0, 0, 0.1 + L * 1.1) };
  }
  box(mat, 0.055, 0.065, 0.14 + L * 0.5, 0, 0.03, 0.02 + L * 0.25); // receiver/body
  box(dark, 0.035, 0.035, L, 0, 0.045, 0.12 + L * 0.5);            // barrel forward
  box(dark, 0.05, 0.10, 0.055, 0, -0.055, -0.01);                  // pistol grip (down/back)
  const magMesh = box(dark, 0.045, 0.085, 0.05, 0, -0.04, 0.03);   // magazine (hidden mid-reload)
  magMesh.name = "mag";
  return {
    mesh: g,
    foreGrip: new THREE.Vector3(0, 0.02, 0.12 + L * 0.55), // support hand on the fore-end
    muzzle: new THREE.Vector3(0, 0.045, 0.12 + L + 0.02),
  };
}
